import { Container } from "@mui/material";
import Alert from "@mui/material/Alert";
import ExpandableTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/ExpandableTable";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import SearchBoxPersisted from "components/SearchBoxPersisted";
import getUserDisplayName from "helpers/userHelper";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import {
  AuditLogEntry,
  ListRequestOfAuditLogRequest,
  OrganizationUserDetails,
} from "services/KnowfullyAdminClient";
import persistanceStore from "stores/persistanceStore";

const UserDesignationsChangeLog = () => {
  const { userId } = useParams<{ userId: string }>();
  const { formatMessage: t } = useIntl();
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("createdOn");
  const searchTerm = persistanceStore.useState(
    (s) => s.userDesignationChangeLogSearchTerm
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const {
    organizationUserService,
    loggingService,
    userDetailsUrl,
  } = useGlobalContext();

  const userResult = useQuery<OrganizationUserDetails>(
    ["getOrganizationUserDetails", userId],
    () => organizationUserService.getOrganizationUserDetails(userId!),
    {
      enabled: !!userId,
    }
  );

  const user = userResult.data;
  const title = t(
    { id: "userDesignationsChangeLogTitle" },
    {
      name: getUserDisplayName(user, t({ id: "user" })),
    }
  );

  useTitle(title);

  const { isError, data, error, isLoading } = useQuery(
    ["getAuditLog", page, rowsPerPage, orderBy, order, userId, searchTerm],
    () =>
      loggingService.getAuditLog(
        ListRequestOfAuditLogRequest.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortDescending: order === "desc",
          sortField: orderBy,
          parameters: {
            userId: userId,
            searchText: searchTerm,
            entityNames: ["Profile Updated", "Designation", "License"], //we need the 'Profile Updated', to be backwards compatible with older logs that don't have the 'Designation' entity name
          },
        })
      ),
    {
      enabled: !!userId,
    }
  );

  if (userResult.isLoading) return <Loader />;

  const rows = (data?.data ?? []).map((row) => ({
    ...row,
    id: row.id!,
  }));

  const cells: Array<TableCellType> = [
    {
      accessor: "logDate",
      type: "date",
      label: t({ id: "timeStampLabel" }),
    },
    {
      accessor: "changedBy",
      type: "node",
      noSort: true,
      label: t({ id: "changedByLabel" }),
      View: ({ value }: { value: AuditLogEntry }) => (
        <>{value.adminUser?.organizationUserId ?? value.user?.organizationUserId ?? ""}</>
      ),
    },
    {
      accessor: "auditLogEntryRows",
      noSort: true,
      type: "node",
      label: t({ id: "changeLabel" }),
      View: ({ value }: { value: AuditLogEntry }) => {
        return (
          <div>
            {value.auditLogEntryRows?.map((entry, index) => (
              <div key={index}>
                <strong>{entry.name}</strong>: {entry.value} (Previous:{" "}
                {entry.priorValue})
              </div>
            ))}
          </div>
        );
      },
    },
  ];

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const handleSetPersistedSearchTerm = (searchText: string): void => {
    persistanceStore.update((s) => {
      s.userDesignationChangeLogSearchTerm = searchText;
    });
  };

  return (
    <Container>
      <RedirectBackButton
        redirectRoute={userDetailsUrl(userId)}
        labelKey="backToUser"
      />

      <h1>{title}</h1>

      <SearchBoxPersisted
        setPersistedSearchTerm={handleSetPersistedSearchTerm}
        persistedSearchTerm={searchTerm}
      />

      <ExpandableTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows,
          loading: isLoading,
          dense: true,
          title: t({ id: "profileChangesLog" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          collapsable: false,
        }}
      />
    </Container>
  );
};

export default UserDesignationsChangeLog;
